import { __rest, __assign } from '../../node_modules/tslib/tslib.es6.js';
import React__default__default, { useContext, useRef, useState, useMemo } from 'react';
import PropTypes__default from 'prop-types';
import { useVisibilityToggler, TOGGLE_DELAY } from '../../hooks/use-visibility-toggler/use-visibility-toggler.js';
import useInlineIcon, { InlineIconPropType } from '../../hooks/use-inline-icon/use-inline-icon.js';
import { useSubMenuInitialFocusManager } from '../../hooks/use-sub-menu-initial-focus-manager/use-sub-menu-initial-focus-manager.js';
import { NavElem } from '../_atoms/nav-elem/nav-elem.js';
import { NAV_ELEMS } from '../_atoms/nav-elem/types.js';
import { HoneyBemContext } from '../../contexts/honey-bem-context.js';
import { SubMenuContext } from '../../contexts/sub-menu-context.js';

var NavItem = function (_a) {
  var extraClasses = _a.extraClasses,
    modifier = _a.modifier,
    role = _a.role,
    linkRole = _a.linkRole,
    href = _a.href,
    RouterLink = _a.RouterLink,
    subMenu = _a.subMenu,
    open = _a.open,
    children = _a.children,
    Elem = _a.Elem,
    linkRef = _a.linkRef,
    InlineIconProp = _a.InlineIcon,
    _b = _a.active,
    active = _b === void 0 ? false : _b,
    _c = _a.openOnHover,
    openOnHover = _c === void 0 ? false : _c,
    rest = __rest(_a, ["extraClasses", "modifier", "role", "linkRole", "href", "RouterLink", "subMenu", "open", "children", "Elem", "linkRef", "InlineIcon", "active", "openOnHover"]);
  var bem = useContext(HoneyBemContext);
  var liRef = useRef(null);
  var _d = useState(),
    lastEvent = _d[0],
    setLastEvent = _d[1];
  var handleToggle = function (_isActive, event) {
    return setLastEvent(event);
  };
  var _e = useVisibilityToggler({
      initialActive: !!open,
      content: liRef.current,
      hoverEvents: openOnHover,
      onToggle: handleToggle,
      arrowEvents: true
    }),
    hookOpen = _e[0],
    events = _e[1],
    closeMenu = _e[2];
  var isOpen = open === undefined ? hookOpen : open;
  var focusAt = useSubMenuInitialFocusManager({
    isExpanded: isOpen,
    lastEvent: lastEvent
  });
  var subMenuContextValue = useMemo(function () {
    var _a;
    return {
      setInitialFocus: focusAt,
      hidden: !isOpen,
      onEscapeCallback: closeMenu,
      target: (_a = liRef === null || liRef === void 0 ? void 0 : liRef.current) === null || _a === void 0 ? void 0 : _a.firstElementChild
    };
  }, [focusAt, isOpen, closeMenu]);
  var handleBlur = function (event) {
    var origin = event.relatedTarget;
    if (origin && liRef.current && liRef.current.contains(origin)) return;
    closeMenu();
  };
  var handleMouseLeave = function () {
    return openOnHover && setTimeout(closeMenu, TOGGLE_DELAY);
  };
  /** attaches target attributes and events for subMenu toggler */
  var restProps = subMenu ? __assign(__assign({
    'aria-haspopup': 'menu',
    'aria-expanded': isOpen,
    role: linkRole
  }, events), rest) : __assign({
    role: linkRole
  }, rest);
  var anchorElem = href ? 'a' : 'span';
  var subMenuElem = subMenu ? 'button' : undefined;
  var itemElem = Elem || subMenuElem || anchorElem;
  var navElementProps = __assign({
    Elem: itemElem,
    className: bem === null || bem === void 0 ? void 0 : bem.e('link', {
      active: active,
      'has-subnav': subMenu,
      'has-subnav-open': isOpen
    }),
    innerRef: linkRef,
    href: href,
    active: active,
    RouterLink: RouterLink
  }, restProps);
  return /*#__PURE__*/React__default__default.createElement("li", {
    role: role,
    ref: liRef,
    className: bem === null || bem === void 0 ? void 0 : bem.e('item', modifier, extraClasses),
    onBlur: handleBlur,
    onMouseLeave: handleMouseLeave
  }, /*#__PURE__*/React__default__default.createElement(NavElem, __assign({}, navElementProps), useInlineIcon(InlineIconProp, !!active), /*#__PURE__*/React__default__default.createElement("span", {
    className: bem === null || bem === void 0 ? void 0 : bem.e('text')
  }, children)), subMenu && /*#__PURE__*/React__default__default.createElement(SubMenuContext.Provider, {
    value: subMenuContextValue
  }, subMenu));
};
NavItem.displayName = 'NavItem';
NavItem.propTypes = {
  extraClasses: PropTypes__default.string,
  modifier: PropTypes__default.string,
  role: PropTypes__default.string,
  linkRole: PropTypes__default.string,
  linkRef: PropTypes__default.oneOfType([PropTypes__default.func, PropTypes__default.shape({
    current: PropTypes__default.oneOfType([PropTypes__default.element, PropTypes__default.object])
  })]),
  href: PropTypes__default.string,
  active: PropTypes__default.bool,
  RouterLink: PropTypes__default.oneOfType([PropTypes__default.func, PropTypes__default.object]),
  subMenu: PropTypes__default.node,
  open: PropTypes__default.bool,
  openOnHover: PropTypes__default.bool,
  children: PropTypes__default.oneOfType([PropTypes__default.string, PropTypes__default.node]).isRequired,
  InlineIcon: InlineIconPropType,
  Elem: PropTypes__default.oneOf(NAV_ELEMS)
};

export { NavItem as default };
